/**
 * Metric ↔ Widget Compatibility + Default-Metric Resolution (Story 37.7)
 *
 * The keystone of the per-integration metric catalog + auto-bind-on-drop
 * feature. Both the picker's metric-selection surface (Task 1) and the
 * auto-bind heuristic that populates a freshly-dropped widget (Task 2) call
 * the SAME `isMetricCompatibleWithWidget` predicate here — there is exactly
 * one compatibility filter, so the card a user sees and the binding they get
 * can never diverge.
 *
 * ── The dual-axis filter (Pre-mortem A / correction #2) ──────────────
 *
 * `MetricDefinition.compatibleWidgets` is OPTIONAL and only sparsely
 * populated (newer adapters like Ahrefs set it; GA4 — the single most-used
 * integration — does NOT). A naive `metric.compatibleWidgets.includes(type)`
 * filter therefore returns ∅ for GA4 and auto-binds nothing. To stay correct
 * the predicate combines two axes:
 *
 *   1. `compatibleWidgets` PRESENT → use it verbatim (the metric declares
 *      exactly which widget types it supports).
 *   2. `compatibleWidgets` ABSENT → fall back to the metric's `category`
 *      against `WIDGET_METRIC_COMPATIBILITY[widgetType]` (the category
 *      allowlist; `["*"]` = all categories).
 *
 * ── The representative-default heuristic (ADR-RE-4 / correction #1) ───
 *
 * `MetricDefinition` has NO `default`/`primary`/`priority` flag, so the
 * representative default must be DERIVED. Adapters already declare their
 * metrics in priority/representativeness order (the same ordering
 * `buildMetricPlatformMap`'s `METRIC_PRIORITY` relies on), so the rule is:
 *
 *   the representative default = the FIRST non-deprecated metric (in adapter
 *   declaration order) that passes the dual-axis filter for the dropped
 *   widget type.
 *
 * Deprecated metrics are never chosen as the default (AC #7) and blank
 * (`undefined` → `metricKey: ""`) is the last resort, never a crash.
 */

import type { MetricDefinition } from "@provenleap/integrations";
import { WIDGET_METRIC_COMPATIBILITY } from "./widgetCompatibility";

/**
 * Returns true iff `metric` is compatible with the given `widgetType`,
 * using the dual-axis rule (see module header).
 *
 * Pure + side-effect-free so it can be unit-tested in isolation and reused
 * by both the picker filter and the auto-bind resolver.
 */
export function isMetricCompatibleWithWidget(
  metric: MetricDefinition,
  widgetType: string,
): boolean {
  // Axis 1: the metric explicitly declares its compatible widget types.
  if (metric.compatibleWidgets && metric.compatibleWidgets.length > 0) {
    return metric.compatibleWidgets.includes(widgetType);
  }

  // Axis 2 (fallback): no explicit declaration → match the metric's category
  // against the widget type's category allowlist. An unknown widget type (not
  // in the map) is treated as "no constraint" so we never silently drop a
  // metric for a type the allowlist simply hasn't catalogued.
  const allowedCategories = WIDGET_METRIC_COMPATIBILITY[widgetType];
  if (!allowedCategories || allowedCategories.includes("*")) return true;

  const category = (metric.category ?? "").toLowerCase();
  if (!category) return false; // category-gated widget + uncategorised metric → not compatible
  return allowedCategories.some((c) => c.toLowerCase() === category);
}

/**
 * Resolves the representative default metric KEY for a freshly-dropped widget
 * of `widgetType`, from the platform's candidate metrics (adapter declaration
 * order). Returns the first non-deprecated metric that passes the dual-axis
 * filter, or `undefined` when none qualifies (caller leaves the config's
 * `metricKey`/`metrics`/`columns` blank — the last resort, never a crash).
 *
 * @param widgetType      the dropped widget's type (e.g. "kpi", "line-chart")
 * @param candidateMetrics the platform's metrics, in adapter declaration order
 */
export function resolveDefaultMetric(
  widgetType: string,
  candidateMetrics: readonly MetricDefinition[],
): MetricDefinition | undefined {
  return candidateMetrics.find(
    (m) => !m.deprecated && isMetricCompatibleWithWidget(m, widgetType),
  );
}

/**
 * Widget types that read their metric binding from `config.metricKey` (a
 * single string). The remaining metric-bound types read it from an array
 * (`metrics: string[]` for the charts, `columns: string[]` for data-table).
 */
const SINGLE_METRIC_KEY_TYPES = new Set(["kpi", "pie-chart"]);
/** Widget types that read their metric binding from `config.metrics: string[]`. */
const METRICS_ARRAY_TYPES = new Set(["line-chart", "bar-chart"]);
/** Widget type that reads its metric binding from `config.columns: string[]`. */
const COLUMNS_ARRAY_TYPE = "data-table";

/**
 * The resolved default metric to seed, plus the context the editor's object
 * shape needs (`integrationId` slug + display `label`).
 */
export interface DefaultMetricSeed {
  key: string;
  label: string;
  integrationId: string;
}

/**
 * Injects the resolved default metric into a per-drop base config using the
 * EXACT seed key each widget type's RENDERER reads (correction #11 / AC #3).
 * The element of every metric ARRAY is a STRING (the renderer + mock contract —
 * `LineChartWidget`/`BarChartWidget`/`mockWidgetData` read `config.metrics`/
 * `config.columns` as `string[]`); writing the object shape into THOSE keys
 * would render `[object Object]` series and poison the preview (Pre-mortem I).
 *
 * - `kpi`, `pie-chart`   → `metricKey: string`
 * - `line-chart`, `bar-chart` → `metrics: [string]` (one element; bar-chart is
 *   single-metric in the editor but stores an array)
 * - `data-table`         → `columns: [string]` (the RENDERER reads `columns`)
 *   AND `metrics: [{key,integrationId,label}]` — the data-table config panel
 *   (`DataSourceSection`, data-table ∈ `MULTI_METRIC_WIDGET_TYPES`) reads its
 *   selection from `config.metrics` as the OBJECT shape, so we mirror the seed
 *   there too or the panel shows an empty "Add a metric…" select after an
 *   auto-bound drop (review finding #6). This is the ONE type where the
 *   object shape is correct (data-table never uses `metrics` for rendering).
 * - everything else (funnel/demographic/top-posts/…) is NOT metric-key-bound;
 *   it renders non-blank from its own defaults + mock data, so we leave its
 *   config untouched.
 *
 * Returns a NEW config object — never mutates the input (so the shared
 * `DEFAULT_WIDGET_CONFIGS` constant is never leaked into).
 *
 * @param baseConfig the per-drop base config (already carries integrationId etc.)
 * @param widgetType the dropped widget's type
 * @param seed       the resolved default metric (key+label+integrationId), or
 *                   `undefined` (blank — leave the type's existing default)
 */
export function applyDefaultMetricToConfig(
  baseConfig: Record<string, unknown>,
  widgetType: string,
  seed: DefaultMetricSeed | undefined,
): Record<string, unknown> {
  // No metric resolved → leave the type's existing blank default in place.
  if (!seed) return { ...baseConfig };

  const next = { ...baseConfig };
  if (SINGLE_METRIC_KEY_TYPES.has(widgetType)) {
    next.metricKey = seed.key;
  } else if (METRICS_ARRAY_TYPES.has(widgetType)) {
    next.metrics = [seed.key];
  } else if (widgetType === COLUMNS_ARRAY_TYPE) {
    // Renderer reads `columns` (string[]); the config panel reads `metrics`
    // (object[]). Seed BOTH so the canvas preview AND the edit panel agree.
    next.columns = [seed.key];
    next.metrics = [{ key: seed.key, integrationId: seed.integrationId, label: seed.label }];
  }
  // Other types are not metric-key-bound — return baseConfig untouched.
  return next;
}
